'use client';

import { useEffect, useMemo, useState } from 'react';
import { CheckIcon, HashIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { ScrollRail } from '@/components/ui/scroll-rail';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { splitTagsByScope, tagChipStyle } from '@/lib/transactions/tags';
import { cn, normalizeForSearch } from '@/lib/utils';
import type { TransactionTag } from '@/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: readonly string[];
  onApply: (value: string[]) => void;
  /** Live preview of how many rows will show given the *pending* selection. */
  previewCount: (pending: readonly string[]) => number;
  /** The catalog (global + personal), from useTags. */
  tags: readonly TransactionTag[];
  /** Per-tag row count within the current visible scope (meta tagFacets). */
  countsByTagId: ReadonlyMap<string, number>;
  /** Per-tag absolute expense volume within the current scope (meta tagFacets). */
  amountsByTagId: ReadonlyMap<string, number>;
  /**
   * Creates a PERSONAL tag from the search text (VER-378: creation is explicit and
   * reachable from the filter too). Resolves with the created tag, which the drawer
   * pre-selects — its CTA then correctly reads "Nenhum lançamento".
   */
  onCreateTag: (name: string) => Promise<TransactionTag>;
}

function pluralize(n: number): string {
  if (n === 0) return 'Nenhum lançamento';
  if (n === 1) return 'Mostrar 1 lançamento';
  return `Mostrar ${n} lançamentos`;
}

/** How many high-usage tags the "Recentes" rail offers before the full list. */
const RAIL_SIZE = 4;

export function TagFilterDrawer({
  open,
  onOpenChange,
  value,
  onApply,
  previewCount,
  tags,
  countsByTagId,
  amountsByTagId,
  onCreateTag,
}: Props) {
  const formatCurrency = useFormatCurrency();
  const [pending, setPending] = useState<Set<string>>(() => new Set(value));
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  // Pending selections follow the applied value (re-synced if it changes while open).
  useEffect(() => {
    if (open) setPending(new Set(value));
  }, [open, value]);

  // Search resets ONLY on (re)open — never on a mid-open `value` identity change.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // Tags visible in the picker: those with rows in the current scope, plus any
  // currently-selected/pending ids (so users can always deselect, and a tag created
  // seconds ago stays visible despite its zero count).
  const visibleTags = useMemo(() => {
    return tags.filter(
      (t) => (countsByTagId.get(t.id) ?? 0) > 0 || value.includes(t.id) || pending.has(t.id),
    );
  }, [tags, countsByTagId, value, pending]);

  const filteredTags = useMemo(() => {
    if (query.length === 0) return visibleTags;
    const needle = normalizeForSearch(query);
    return visibleTags.filter((t) => normalizeForSearch(t.name).includes(needle));
  }, [visibleTags, query]);

  const sections = useMemo(() => splitTagsByScope(filteredTags), [filteredTags]);

  // "Recentes" = the highest-usage tags in the scope (the real recency signal would
  // need telemetry; usage is the honest stand-in, same spirit as the category stub).
  const railTags = useMemo(
    () =>
      [...visibleTags]
        .sort((a, b) => (countsByTagId.get(b.id) ?? 0) - (countsByTagId.get(a.id) ?? 0))
        .slice(0, RAIL_SIZE),
    [visibleTags, countsByTagId],
  );

  const normalizedQuery = normalizeForSearch(query.trim());
  const exactMatch = tags.some((t) => normalizeForSearch(t.name) === normalizedQuery);
  const showCreateRow = query.trim().length > 0 && !exactMatch;

  const toggle = (id: string) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await onCreateTag(query.trim());
      setPending((prev) => new Set(prev).add(created.id));
      setQuery('');
    } catch {
      // The host surfaces the failure (toast); the drawer just stays as it was.
    } finally {
      setCreating(false);
    }
  };

  const liveCount = previewCount(Array.from(pending));

  const renderRow = (tag: TransactionTag) => {
    const style = tagChipStyle(tag);
    const selected = pending.has(tag.id);
    const count = countsByTagId.get(tag.id) ?? 0;
    const amount = amountsByTagId.get(tag.id) ?? 0;
    return (
      <li
        key={tag.id}
        className={cn(selected ? 'bg-primary/5' : 'hover:bg-surface-sunken')}
      >
        <button
          type="button"
          onClick={() => toggle(tag.id)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Remover' : 'Selecionar'} #${tag.name}`}
          data-testid={`tag-filter-option-${tag.id}`}
          className="flex w-full min-w-0 items-center gap-3 py-3 pr-4 pl-4 text-left"
        >
          <span
            className={cn(
              'flex size-5 shrink-0 items-center justify-center rounded-md border',
              selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong bg-surface',
            )}
            aria-hidden
          >
            {selected && <CheckIcon className="size-3.5" weight="bold" />}
          </span>
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: style.backgroundColor }}
            aria-hidden
          >
            <HashIcon className="size-4" weight="bold" style={{ color: style.color }} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-sm font-semibold text-text-primary">#{tag.name}</span>
              {tag.scope === 'global' && (
                <span
                  data-testid="tag-scope-badge"
                  className="shrink-0 rounded-full bg-surface-sunken px-1.5 py-0.5 text-3xs font-medium text-text-tertiary"
                >
                  Vértice
                </span>
              )}
            </span>
            {count > 0 && (
              <span className="mt-0.5 block text-xs text-text-tertiary">
                {count === 1 ? '1 lançamento' : `${count} lançamentos`}
                {amount > 0 && (
                  <>
                    <span className="px-1">·</span>
                    <span className="font-mono tabular-nums">{formatCurrency(amount)}</span>
                  </>
                )}
              </span>
            )}
          </span>
        </button>
      </li>
    );
  };

  const renderSection = (title: string, list: TransactionTag[], testid: string) =>
    list.length > 0 && (
      <>
        <p
          data-testid={testid}
          className="px-4 pt-3 pb-1 text-2xs font-bold tracking-wider text-text-tertiary uppercase"
        >
          {title}
        </p>
        <ul className="divide-y divide-divider" role="list">
          {list.map(renderRow)}
        </ul>
      </>
    );

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        data-tokens="v2"
        className="flex size-full min-h-0 flex-col overflow-hidden bg-surface px-0 data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:size-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="flex flex-row items-center justify-between px-4 py-3">
          <DrawerTitle className="text-lg font-semibold text-text-primary">Tags</DrawerTitle>
          <DrawerDescription className="sr-only">
            Selecione as tags usadas para filtrar os lançamentos.
          </DrawerDescription>
          <button
            type="button"
            data-testid="tag-filter-clear"
            className="text-sm text-text-secondary hover:text-text-primary"
            onClick={() => setPending(new Set())}
          >
            Limpar
          </button>
        </DrawerHeader>

        <div className="flex items-center gap-2 border-t border-border px-3 py-2">
          <div className="flex flex-1 items-center gap-2 rounded-md bg-surface-sunken px-3">
            <MagnifyingGlassIcon className="size-4 text-text-tertiary" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar tag"
              aria-label="Buscar tag"
              data-testid="tag-filter-search"
              className="h-9 w-full bg-transparent text-base outline-none placeholder:text-text-tertiary"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {query.length === 0 && railTags.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <p className="px-1 pb-2 text-2xs font-bold tracking-wider text-text-tertiary uppercase">
                Recentes
              </p>
              <ScrollRail railClassName="gap-2 pb-1">
                {railTags.map((tag) => {
                  const selected = pending.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggle(tag.id)}
                      aria-pressed={selected}
                      data-testid={`tag-recent-${tag.id}`}
                      className={cn(
                        'flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                        selected ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-surface text-text-primary',
                      )}
                    >
                      #{tag.name}
                    </button>
                  );
                })}
              </ScrollRail>
            </div>
          )}

          {filteredTags.length === 0 && !showCreateRow ? (
            <p className="px-4 py-10 text-center text-sm text-text-tertiary">
              Nenhuma tag encontrada
            </p>
          ) : (
            <>
              {renderSection('Suas tags', sections.personal, 'tag-section-personal')}
              {renderSection('Tags da Vértice', sections.global, 'tag-section-global')}
            </>
          )}

          {showCreateRow && (
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating}
              data-testid="tag-filter-create"
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-brand-primary transition-colors hover:bg-brand-50 disabled:opacity-60"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-dashed border-border">
                <PlusIcon className="size-4" weight="bold" />
              </span>
              <span className="text-sm font-semibold">
                {creating ? 'Criando…' : `Criar tag “${query.trim()}”`}
              </span>
            </button>
          )}
        </div>

        <div className="border-t border-border px-4 pt-3">
          <button
            type="button"
            data-testid="tag-filter-cta"
            onClick={() => {
              onApply(Array.from(pending));
              onOpenChange(false);
            }}
            className="h-12 w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            disabled={liveCount === 0}
          >
            {pluralize(liveCount)}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
